/* artie — theme behaviour. No dependencies. Everything is progressive:
   the page works as plain HTML, this file makes it nicer. */
(() => {
  'use strict';

  const A = window.Artie || {};
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- helpers ---------- */
  function money(cents) {
    const format = (A.moneyFormat || '${{amount}}').replace(/<[^>]*>/g, '');
    const n = (cents || 0) / 100;
    const withDelims = (value, decimals, thousands, decimal) => {
      const parts = value.toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.join(decimal);
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return withDelims(n, 0, ',', '.');
        case 'amount_with_comma_separator': return withDelims(n, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelims(n, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return withDelims(n, 2, "'", '.');
        case 'amount_no_decimals_with_space_separator': return withDelims(n, 0, ' ', ',');
        case 'amount_with_space_separator': return withDelims(n, 2, ' ', ',');
        default: return withDelims(n, 2, ',', '.');
      }
    });
  }

  function priceHTML(price, compare) {
    const sale = compare > price;
    return `<span class="price${sale ? ' price--sale' : ''}"><span class="price__now">${money(price)}</span>${sale ? `<s class="price__was">${money(compare)}</s>` : ''}</span>`;
  }

  async function fetchDoc(url) {
    const res = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    if (!res.ok) throw new Error(res.status);
    return new DOMParser().parseFromString(await res.text(), 'text/html');
  }

  let toastTimer;
  function toast(message) {
    const el = $('.toast');
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => { el.hidden = true; }, 3600);
  }

  /* ---------- tint: the picked colour becomes the light ---------- */
  function hexToRgb(hex) {
    const h = hex.replace('#', '');
    const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
    const int = parseInt(full, 16);
    return [(int >> 16) & 255, (int >> 8) & 255, int & 255];
  }
  function luminance([r, g, b]) {
    const f = (v) => { v /= 255; return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4; };
    return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
  }
  function setTint(scope, hex) {
    if (!scope || !/^#[0-9a-f]{3,8}$/i.test(hex || '')) return;
    const rgb = hexToRgb(hex);
    const lum = luminance(rgb);
    // dark colours get lifted so the light is still visible on black
    const lift = lum < 0.18 ? 0.5 : 0;
    const light = rgb.map((v) => Math.round(v + (255 - v) * lift));
    scope.style.setProperty('--tint', `rgb(${light.join(' ')})`);
    scope.style.setProperty('--tint-ui', lum < 0.2 ? 'var(--cotton)' : hex);
    $$('.tube', scope).forEach((tube) => {
      if (reduceMotion) return;
      tube.classList.remove('is-flick');
      void tube.offsetWidth;
      tube.classList.add('is-flick');
    });
  }

  /* ---------- dialogs ---------- */
  function openDialog(id) {
    const dialog = document.getElementById(id);
    if (!dialog || dialog.open) return dialog;
    dialog.showModal();
    dialog.dispatchEvent(new CustomEvent('dialog:open'));
    return dialog;
  }
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-open]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.open);
      if (dialog) {
        event.preventDefault();
        opener.closest('dialog')?.close();
        openDialog(opener.dataset.open);
      }
      return;
    }
    const closer = event.target.closest('[data-close]');
    if (closer) { closer.closest('dialog')?.close(); return; }
    // click on the backdrop
    if (event.target instanceof HTMLDialogElement) {
      const r = event.target.getBoundingClientRect();
      const inside = event.clientX >= r.left && event.clientX <= r.right && event.clientY >= r.top && event.clientY <= r.bottom;
      if (!inside) event.target.close();
    }
  });

  /* ---------- announcement bar ---------- */
  customElements.define('announce-bar', class extends HTMLElement {
    connectedCallback() {
      const items = $$('.announce__item', this);
      if (items.length < 2 || reduceMotion) return;
      let i = 0;
      let paused = false;
      this.addEventListener('pointerenter', () => { paused = true; });
      this.addEventListener('pointerleave', () => { paused = false; });
      this.addEventListener('focusin', () => { paused = true; });
      this.addEventListener('focusout', () => { paused = false; });
      this.timer = setInterval(() => {
        if (paused || document.hidden) return;
        items[i].classList.remove('is-on');
        i = (i + 1) % items.length;
        items[i].classList.add('is-on');
      }, (Number(this.dataset.interval) || 5) * 1000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
  });

  /* ---------- header ---------- */
  customElements.define('site-header', class extends HTMLElement {
    connectedCallback() {
      let last = window.scrollY;
      const onScroll = () => {
        const y = window.scrollY;
        this.classList.toggle('is-stuck', y > 24);
        const megaOpen = this.classList.contains('is-open');
        const hide = !megaOpen && y > 400 && y > last;
        this.classList.toggle('is-hidden', hide);
        document.documentElement.classList.toggle('header-hidden', hide);
        last = y;
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();

      const toggles = $$('[data-mega-toggle]', this);
      const close = () => {
        toggles.forEach((t) => {
          t.setAttribute('aria-expanded', 'false');
          document.getElementById(t.getAttribute('aria-controls')).hidden = true;
        });
        this.classList.remove('is-open');
      };
      const open = (toggle) => {
        close();
        toggle.setAttribute('aria-expanded', 'true');
        document.getElementById(toggle.getAttribute('aria-controls')).hidden = false;
        this.classList.add('is-open');
      };
      toggles.forEach((toggle) => {
        toggle.addEventListener('click', () => (toggle.getAttribute('aria-expanded') === 'true' ? close() : open(toggle)));
        toggle.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') open(toggle); });
      });
      this.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') close(); });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
      document.addEventListener('click', (e) => { if (!this.contains(e.target)) close(); });
    }
  });

  /* ---------- film (hero + split) ---------- */
  customElements.define('hero-film', class extends HTMLElement {
    connectedCallback() {
      this.video = $('video', this);
      this.toggle = $('[data-film-toggle]', this);
      if (!this.video) return;
      const saveData = navigator.connection && navigator.connection.saveData;
      if (reduceMotion || saveData) return; // the poster is the film
      const start = () => ('requestIdleCallback' in window ? requestIdleCallback(() => this.load(), { timeout: 2500 }) : setTimeout(() => this.load(), 600));
      if (document.readyState === 'complete') start();
      else window.addEventListener('load', start, { once: true });
    }
    load() {
      let sources = [];
      try { sources = JSON.parse(this.video.dataset.sources).filter((s) => s.url); } catch (e) { return; }
      if (!sources.length) return;
      sources.sort((a, b) => a.h - b.h);
      const want = Math.min(1080, Math.max(480, this.clientWidth * Math.min(window.devicePixelRatio || 1, 2) * 0.5625));
      const pick = sources.find((s) => s.h >= want) || sources[sources.length - 1];
      this.video.src = pick.url;
      this.userPaused = false;
      this.video.addEventListener('playing', () => this.classList.add('is-playing'), { once: true });
      new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting && !this.userPaused) this.video.play().catch(() => {});
        else this.video.pause();
      }, { threshold: 0.15 }).observe(this);
      if (this.toggle) {
        this.toggle.hidden = false;
        this.toggle.addEventListener('click', () => {
          this.userPaused = !this.userPaused;
          this.toggle.setAttribute('aria-pressed', String(this.userPaused));
          if (this.userPaused) this.video.pause(); else this.video.play().catch(() => {});
        });
      }
    }
  });

  /* ---------- rails ---------- */
  customElements.define('scroll-rail', class extends HTMLElement {});
  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-rail-prev], [data-rail-next]');
    if (!btn) return;
    const rail = $('scroll-rail', btn.closest('.rail, [data-section]'));
    if (!rail) return;
    const dir = btn.hasAttribute('data-rail-next') ? 1 : -1;
    rail.scrollBy({ left: dir * rail.clientWidth * 0.8, behavior: reduceMotion ? 'auto' : 'smooth' });
  });

  /* ---------- category index (marker element only; the effect is CSS) ---------- */
  customElements.define('category-index', class extends HTMLElement {});

  /* ---------- product unit: pickers, price, image, tint ---------- */
  class ProductUnit {
    constructor(root) {
      this.root = root;
      root._unit = this;
      try { this.variants = JSON.parse($('[data-variants]', root).textContent); } catch (e) { this.variants = []; }
      this.fieldsets = $$('fieldset[data-option]', root);
      this.isMain = root.hasAttribute('data-main-product');
      this.ownScope = root.hasAttribute('data-tint-scope');
      this.scope = this.ownScope ? root : this.isMain ? document.documentElement : (root.closest('dialog, [data-section]') || root);
      // a link that only chose a colour (card dots, the colour wall, quick add) must not choose a size for the customer
      const picksColourOnly = this.isMain ? new URL(location.href).searchParams.get('pick') === 'size' : !root.hasAttribute('data-keep-size');
      if (picksColourOnly) this.openSize();
      root.addEventListener('change', (e) => { if (e.target.matches('fieldset[data-option] input')) this.update(true); });
      this.update(false);
    }
    openSize() {
      this.fieldsets.forEach((fs) => {
        if (fs.classList.contains('opt--colour')) return;
        const inputs = $$('input', fs);
        if (inputs.length > 1) inputs.forEach((input) => { input.checked = false; });
      });
    }
    get selected() {
      return this.fieldsets.map((fs) => $('input:checked', fs)?.value);
    }
    get missing() {
      return this.fieldsets.filter((fs) => !$('input:checked', fs));
    }
    // the exact variant, once every option is chosen
    get variant() {
      const id = Number($('[data-variant-id]', this.root)?.value);
      return this.variants.find((v) => v.id === id);
    }
    find(options) {
      return this.variants.find((v) => v.o.every((o, i) => o === options[i]));
    }
    // every variant that fits the options chosen so far
    loose(options) {
      return this.variants.filter((v) => options.every((o, i) => o == null || v.o[i] === o));
    }
    select(optionIndex, value) {
      const fs = this.fieldsets[optionIndex];
      if (!fs) return false;
      const input = $$('input', fs).find((i) => i.value.toLowerCase() === String(value).toLowerCase());
      if (!input) return false;
      input.checked = true;
      this.update(true);
      return true;
    }
    colourIndex() {
      return this.fieldsets.findIndex((fs) => fs.classList.contains('opt--colour'));
    }
    // points at the option still to choose; true when there was one
    ask() {
      const fs = this.missing[0];
      if (!fs) return false;
      fs.classList.remove('is-wanted');
      void fs.offsetWidth;
      fs.classList.add('is-wanted');
      const r = fs.getBoundingClientRect();
      if (r.top < 90 || r.bottom > window.innerHeight - 100) fs.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
      return true;
    }
    update(userAction) {
      const options = this.selected;
      const missing = this.missing;
      const variant = missing.length ? undefined : this.fieldsets.length ? this.find(options) : this.variants[0];
      const pool = this.loose(options);
      const view = variant || pool.find((v) => v.a) || pool[0]; // what to show while a size is still to choose
      this.view = view;
      this.root.classList.toggle('needs-size', missing.length > 0);

      // mark values that do not exist or are sold out with the current other choices
      this.fieldsets.forEach((fs, i) => {
        $$('input', fs).forEach((input) => {
          const probe = [...options];
          probe[i] = input.value;
          input.parentElement.classList.toggle('is-off', !this.loose(probe).some((v) => v.a));
        });
        const current = $('[data-option-current]', fs);
        const checked = $('input:checked', fs);
        if (current && checked) current.textContent = checked.value.toLowerCase();
        if (checked) fs.classList.remove('is-wanted');
      });

      const colourInput = $('.opt--colour input:checked', this.root);
      if (colourInput && colourInput.dataset.hex && (userAction || this.isMain || this.ownScope || this.root.closest('dialog'))) {
        setTint(this.scope, colourInput.dataset.hex);
      }

      $$('[data-add]', this.root).forEach((btn) => {
        const label = $('[data-add-label]', btn);
        if (missing.length) {
          btn.disabled = false;
          if (label) label.textContent = A.strings.selectSize;
          return;
        }
        btn.disabled = !variant || !variant.a;
        if (label) label.textContent = !variant ? A.strings.unavailable : variant.a ? A.strings.addToCart : A.strings.soldOut;
      });
      $$('[data-variant-id]', this.root).forEach((input) => { input.value = variant ? variant.id : ''; });

      if (view) {
        $$('[data-price]', this.root).forEach((el) => { el.innerHTML = priceHTML(view.p, view.c); });
        const img = $('[data-product-img]', this.root);
        if (img && view.i && userAction) { img.removeAttribute('srcset'); img.src = view.i; }
      }

      if (this.isMain && view) {
        if (userAction && variant) {
          const url = new URL(location.href);
          url.searchParams.set('variant', variant.id);
          url.searchParams.delete('pick');
          history.replaceState({}, '', url);
        }
        const gallery = $('product-gallery', this.root);
        if (gallery && view.m && (userAction || new URL(location.href).searchParams.has('variant'))) {
          gallery.goTo(String(view.m), !userAction);
        }
      }
      this.root.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));
    }
  }
  function initProducts(root = document) {
    $$('[data-product]', root).forEach((el) => { if (!el._unit) new ProductUnit(el); });
  }

  /* ---------- gallery ---------- */
  customElements.define('product-gallery', class extends HTMLElement {
    connectedCallback() {
      this.stage = $('[data-stage]', this);
      this.slides = $$('.gallery__slide', this);
      this.thumbs = $$('[data-thumb]', this);
      this.counter = $('[data-gallery-index]', this);
      this.index = 0;
      let raf;
      this.stage.addEventListener('scroll', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => this.sync());
      }, { passive: true });
      this.addEventListener('click', (e) => {
        const thumb = e.target.closest('[data-thumb]');
        if (thumb) return this.goTo(thumb.dataset.thumb);
        if (e.target.closest('[data-gallery-prev]')) return this.step(-1);
        if (e.target.closest('[data-gallery-next]')) return this.step(1);
        const zoom = e.target.closest('[data-zoom]');
        if (zoom) {
          const dialog = openDialog('Zoom');
          const img = $('[data-zoom-img]', dialog);
          img.src = zoom.dataset.zoom;
          const scroller = $('.zoom__scroll', dialog);
          img.onload = () => { scroller.scrollLeft = (scroller.scrollWidth - scroller.clientWidth) / 2; };
          scroller.onclick = () => dialog.close();
        }
      });
      this.stage.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this.step(1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.step(-1); }
      });
    }
    sync() {
      const i = Math.round(this.stage.scrollLeft / this.stage.clientWidth);
      if (i === this.index) return;
      this.index = i;
      if (this.counter) this.counter.textContent = i + 1;
      this.thumbs.forEach((t, n) => t.classList.toggle('is-on', n === i));
      const thumb = this.thumbs[i];
      const strip = thumb && thumb.parentElement;
      if (strip) strip.scrollTo({ left: thumb.offsetLeft - strip.clientWidth / 2 + thumb.clientWidth / 2, behavior: reduceMotion ? 'auto' : 'smooth' });
    }
    step(dir) {
      const next = Math.max(0, Math.min(this.slides.length - 1, this.index + dir));
      this.scrollToIndex(next);
    }
    goTo(mediaId, instant) {
      const i = this.slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
      if (i >= 0) this.scrollToIndex(i, instant);
    }
    scrollToIndex(i, instant) {
      this.stage.scrollTo({ left: i * this.stage.clientWidth, behavior: instant || reduceMotion ? 'auto' : 'smooth' });
    }
  });

  /* ---------- cart ---------- */
  const Cart = {
    sectionIds() {
      return [...new Set($$('cart-root').map((el) => el.dataset.section))];
    },
    async add(items, trigger) {
      trigger?.classList.add('is-loading');
      try {
        const res = await fetch(A.routes.cartAdd, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items, sections: this.sectionIds().join(','), sections_url: location.pathname })
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || data.message || A.strings.cartError);
        this.render(data.sections);
        const label = trigger && $('[data-add-label]', trigger);
        if (label) {
          const before = label.textContent;
          label.textContent = A.strings.added;
          setTimeout(() => { if (label.textContent === A.strings.added) label.textContent = before; }, 1600);
        }
        const bag = $('.header__cart');
        if (bag) { bag.classList.remove('is-bump'); void bag.offsetWidth; bag.classList.add('is-bump'); }
        document.getElementById('QuickAdd')?.close();
        if (!document.body.classList.contains('t-cart')) openDialog('CartDrawer');
        return true;
      } catch (error) {
        toast(error.message || A.strings.cartError);
        return false;
      } finally {
        trigger?.classList.remove('is-loading');
      }
    },
    async change(line, quantity) {
      try {
        const res = await fetch(A.routes.cartChange, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line, quantity, sections: this.sectionIds().join(','), sections_url: location.pathname })
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || data.message || A.strings.cartError);
        this.render(data.sections);
      } catch (error) {
        toast(error.message || A.strings.cartError);
        $$('.line.is-busy').forEach((l) => l.classList.remove('is-busy'));
      }
    },
    render(sections) {
      if (!sections) return;
      $$('cart-root').forEach((root) => {
        const html = sections[root.dataset.section];
        if (!html) return;
        const fresh = $('cart-root', new DOMParser().parseFromString(html, 'text/html'));
        if (!fresh) return;
        const body = $('.drawer__body', root);
        const scroll = body ? body.scrollTop : 0;
        root.innerHTML = fresh.innerHTML;
        initEta(root);
        Object.assign(root.dataset, fresh.dataset);
        root.className = fresh.className;
        const newBody = $('.drawer__body', root);
        if (newBody) newBody.scrollTop = scroll;
        root.refresh();
      });
    }
  };

  customElements.define('cart-root', class extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-qty]');
        if (!btn) return;
        const line = btn.closest('.line');
        line.classList.add('is-busy');
        Cart.change(Number(line.dataset.line), Number(btn.dataset.qty));
      });
      this.refresh();
    }
    refresh() {
      const count = Number(this.dataset.count) || 0;
      $$('[data-cart-count]').forEach((el) => { el.textContent = count; el.hidden = count === 0; });
      this.shipbar();
      this.upsell();
    }
    shipbar() {
      const bar = $('[data-shipbar]', this);
      if (!bar || !A.freeShipping) return;
      const rate = Number(window.Shopify?.currency?.rate) || 1;
      // exact in the shop currency, rounded to a whole unit once converted (43.50 EUR reads as $50)
      const threshold = rate === 1 ? A.freeShipping : Math.round((A.freeShipping * rate) / 100) * 100;
      const total = Number(this.dataset.total) || 0;
      const left = threshold - total;
      const text = $('[data-shipbar-text]', bar);
      text.innerHTML = left > 0 ? A.strings.shipAway.replace('[amount]', `<strong>${money(left)}</strong>`) : A.strings.shipDone;
      bar.hidden = false;
      requestAnimationFrame(() => { $('[data-shipbar-fill]', bar).style.width = `${Math.min(100, (total / threshold) * 100)}%`; });
    }
    async upsell() {
      const box = $('[data-upsell]', this);
      const productId = this.dataset.firstProduct;
      if (!box || !productId) return;
      try {
        const doc = await fetchDoc(`${A.routes.recommendations}?product_id=${productId}&limit=4&intent=complementary&section_id=cart-upsell`);
        const inCart = $$('.line', this).map((l) => l.dataset.productId);
        const items = $$('.upsell__item', doc);
        const list = $('[data-upsell-list]', box);
        if (!list) return;
        list.replaceChildren(...items.filter((item) => !inCart.includes(item.dataset.productId)).slice(0, 2));
        box.hidden = list.children.length === 0 || inCart.length > 3;
      } catch (e) { /* suggestions are optional */ }
    }
  });

  // add to cart: product forms and quick add
  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-product-form]');
    if (!form) return;
    if (event.submitter && event.submitter.name !== 'add') return; // express checkout buttons keep their own behaviour
    event.preventDefault();
    const id = Number(new FormData(form).get('id'));
    if (!id) { form.closest('[data-product]')?._unit?.ask(); return; }
    Cart.add([{ id, quantity: 1 }], event.submitter || $('[data-add]', form));
  });
  document.addEventListener('click', (event) => {
    // the add button never fires a native submit: the store's trackers count a submit as an add to cart,
    // which doubled every real add and counted taps made before a size was chosen
    const addButton = event.target.closest('button[name="add"]');
    if (addButton && addButton.form && addButton.form.matches('[data-product-form]')) {
      event.preventDefault();
      const id = Number(new FormData(addButton.form).get('id'));
      if (!id) { addButton.form.closest('[data-product]')?._unit?.ask(); return; }
      Cart.add([{ id, quantity: 1 }], addButton);
      return;
    }
    const quickSubmit = event.target.closest('[data-quick-submit]');
    if (quickSubmit) {
      const unit = quickSubmit.closest('[data-product]')?._unit;
      if (unit?.variant) Cart.add([{ id: unit.variant.id, quantity: 1 }], quickSubmit);
      else unit?.ask();
      return;
    }
    const guard = event.target.closest('[data-ask-size]');
    if (guard) { guard.closest('[data-product]')?._unit?.ask(); return; }
    const oneTap = event.target.closest('[data-add-variant]');
    if (oneTap) { Cart.add([{ id: Number(oneTap.dataset.addVariant), quantity: 1 }], oneTap); return; }
    const quick = event.target.closest('[data-quick-add]');
    if (quick) {
      event.preventDefault();
      openQuickAdd(quick.dataset.quickAdd, quick.hasAttribute('data-keep-size'));
    }
  });

  async function openQuickAdd(productUrl, keepSize) {
    const from = document.querySelector('dialog[open]:not(#QuickAdd)');
    const dialog = document.getElementById('QuickAdd');
    const body = $('[data-quick-body]', dialog);
    const url = new URL(productUrl, location.origin);
    url.searchParams.set('section_id', 'quick-add');
    body.innerHTML = '';
    dialog.style.removeProperty('--tint');
    dialog.style.removeProperty('--tint-ui');
    if (from) from.close();
    openDialog('QuickAdd');
    try {
      const doc = await fetchDoc(url);
      const quick = $('.quick', doc);
      if (!quick) throw new Error('empty');
      if (keepSize) quick.setAttribute('data-keep-size', '');
      body.replaceChildren(quick);
      initProducts(body);
    } catch (e) {
      location.href = productUrl;
    }
  }

  /* ---------- product cards: colour dots ---------- */
  document.addEventListener('click', (event) => {
    const dot = event.target.closest('.card .dot');
    if (!dot) return;
    const card = dot.closest('.card');
    $$('.dot', card).forEach((d) => d.classList.toggle('is-on', d === dot));
    const img = $('[data-card-img]', card);
    if (img && dot.dataset.img) {
      img.removeAttribute('srcset');
      img.src = dot.dataset.img;
      card.classList.add('is-picked');
    }
    if (dot.dataset.url) {
      $$('a[href]', card).forEach((a) => { if (!a.classList.contains('card__more')) a.href = dot.dataset.url; });
      const quick = $('[data-quick-add]', card);
      if (quick) quick.dataset.quickAdd = dot.dataset.url;
    }
  });

  /* ---------- colour wall ---------- */
  customElements.define('colour-wall', class extends HTMLElement {
    connectedCallback() {
      this.img = $('[data-wall-img]', this);
      this.link = $('[data-wall-link]', this);
      this.name = $('[data-wall-name]', this);
      this.words = $$('.wall__word', this);
      this.scope = this.closest('[data-section]') || this;
      const mouse = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      this.words.forEach((word) => {
        word.addEventListener('click', () => this.pick(word));
        if (mouse) word.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') this.pick(word); });
        word.addEventListener('focus', () => { if (word.matches(':focus-visible')) this.pick(word); });
      });
      const first = this.words.find((w) => w.classList.contains('is-on'));
      if (first) setTint(this.scope, first.dataset.hex);
      // a quick roll call the first time the wall is seen, so it is obvious the names are switches
      // mouse only: on a phone the page is being scrolled, and colours changing on their own read as a glitch
      if (!reduceMotion && this.words.length > 6 && mouse) {
        const stop = () => { this.touched = true; };
        this.addEventListener('pointerdown', stop);
        this.addEventListener('pointermove', stop, { once: true });
        new IntersectionObserver(([entry], io) => {
          if (!entry.isIntersecting) return;
          io.disconnect();
          const n = this.words.length;
          const order = [Math.floor(n * 0.2), Math.floor(n * 0.45), Math.floor(n * 0.7), n - 1, Math.floor(n * 0.55), 0];
          order.forEach((index, step) => setTimeout(() => { if (!this.touched) this.pick(this.words[index]); }, 500 + step * 420));
        }, { threshold: 0.55 }).observe($('.wall__words', this));
      }
    }
    pick(word) {
      if (word === this.current) return;
      this.current = word;
      this.words.forEach((w) => {
        w.classList.toggle('is-on', w === word);
        w.setAttribute('aria-checked', String(w === word));
      });
      setTint(this.scope, word.dataset.hex);
      if (this.name) this.name.textContent = word.dataset.name;
      if (this.link) this.link.href = word.dataset.url;
      const src = word.dataset.img;
      if (!src || !this.img) return;
      const token = (this.token = Symbol('swap'));
      this.img.classList.add('is-swapping');
      const next = new Image();
      next.src = src;
      const done = () => {
        if (this.token !== token) return;
        this.img.removeAttribute('srcset');
        this.img.src = src;
        this.img.classList.remove('is-swapping');
      };
      (next.decode ? next.decode() : Promise.resolve()).then(done, done);
    }
  });

  /* ---------- fit builder ---------- */
  customElements.define('fit-builder', class extends HTMLElement {
    connectedCallback() {
      requestAnimationFrame(() => {
        this.tops = $$('[data-half="top"]', this);
        this.top = this.tops.find((el) => !el.hidden)?._unit;
        this.bottom = $('[data-half="bottom"]', this)?._unit;
        if (!this.top || !this.bottom) return;
        this.addEventListener('variant:change', () => this.total());
        $('[data-fit-match]', this)?.addEventListener('click', () => this.match());
        $('[data-fit-add]', this)?.addEventListener('click', (e) => this.add(e.currentTarget));
        this.addEventListener('click', (e) => {
          const btn = e.target.closest('[data-fit-swap]');
          if (btn) this.swap(btn.dataset.fitSwap);
        });
        this.total();
      });
    }
    total() {
      const a = this.top.variant || this.top.view;
      const b = this.bottom.variant || this.bottom.view;
      const out = $('[data-fit-total]', this);
      const pct = this.top.root.hasAttribute('data-in-set') ? Number(this.dataset.setPercent) || 0 : 0;
      if (out && a && b) {
        const save = Math.floor((b.p * pct) / 100); // Shopify rounds the discount down
        out.innerHTML = save ? `<s>${money(a.p + b.p)}</s> ${money(a.p + b.p - save)}` : money(a.p + b.p);
        const deal = $('[data-fit-deal]', this);
        if (deal) deal.hidden = !save;
      }
      const btn = $('[data-fit-add]', this);
      const waiting = this.top.missing.length || this.bottom.missing.length;
      if (btn) btn.disabled = !waiting && !(a && b && a.a && b.a);
    }
    // swap the top for the other one, keeping colour and size when the other one has them
    swap(slot) {
      const next = this.tops.find((el) => el.dataset.slot === slot);
      if (!next || !next._unit || next._unit === this.top) return;
      const from = this.top;
      const to = next._unit;
      const picks = from.selected;
      from.fieldsets.forEach((fs, i) => {
        const j = fs.classList.contains('opt--colour') ? to.colourIndex() : i;
        if (j >= 0 && picks[i]) to.select(j, picks[i]);
      });
      from.root.hidden = true;
      next.hidden = false;
      this.top = to;
      $$('[data-fit-swap]', this).forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.fitSwap === slot)));
      $(`[data-fit-swap="${slot}"]`, next)?.focus({ preventScroll: true });
      this.total();
    }
    match() {
      const ti = this.top.colourIndex();
      const bi = this.bottom.colourIndex();
      if (ti < 0 || bi < 0) return;
      const colour = this.top.selected[ti];
      if (!this.bottom.select(bi, colour)) toast(`no ${colour.toLowerCase()} for the bottom half. clash it is.`);
    }
    add(button) {
      if (this.top.ask() || this.bottom.ask()) return;
      const a = this.top.variant;
      const b = this.bottom.variant;
      if (a && b) Cart.add([{ id: a.id, quantity: 1 }, { id: b.id, quantity: 1 }], button);
    }
  });

  /* ---------- recommendations ---------- */
  customElements.define('product-recs', class extends HTMLElement {
    connectedCallback() {
      if (this.loaded) return;
      new IntersectionObserver(async ([entry], observer) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        this.loaded = true;
        try {
          const doc = await fetchDoc(this.dataset.url);
          const source = $('[data-recs]', doc) || $('.shopify-section', doc) || doc.body;
          const target = $('[data-recs]', this);
          if (source && source.innerHTML.trim()) {
            target.innerHTML = source.innerHTML;
            const role = this.dataset.setRole;
            $$('[data-set-tag]', target).forEach((tag) => {
              const item = tag.closest('.upsell__item');
              const pairs = role && item.dataset.setRole && item.dataset.setRole !== role;
              tag.hidden = !pairs;
              if (pairs) tag.textContent = A.strings.setSave.replace('[amount]', role === 'bottom' ? this.dataset.setSave : item.dataset.setOwn);
            });
            this.hidden = false;
            initMotion(this);
          }
        } catch (e) { /* optional */ }
      }, { rootMargin: '0px 0px 600px 0px' }).observe(this.hidden ? this.parentElement : this);
    }
  });

  /* ---------- sticky buy bar ---------- */
  function initBuyBar() {
    const bar = $('[data-buybar]');
    const main = $('.pdp__buy [data-add]');
    if (!bar || !main) return;
    new IntersectionObserver(([entry]) => {
      const phone = window.matchMedia('(max-width: 989px)').matches;
      bar.hidden = entry.isIntersecting || (entry.boundingClientRect.top > 0 && !phone);
    }).observe(main);
  }

  /* ---------- size finder ---------- */
  customElements.define('size-finder', class extends HTMLElement {
    connectedCallback() {
      const guide = this.parentElement.querySelector('[data-guide]');
      this.rows = this.parse(guide);
      if (!this.rows.length) return;
      this.hidden = false;
      this.strings = $('[data-strings]', this).dataset;
      const imperial = (window.Shopify?.country || '') === 'US';
      if (imperial) $('input[value="imperial"]', this).checked = true;
      this.units();
      this.addEventListener('change', (e) => { if (e.target.name === 'finder-units') this.units(); });
      this.addEventListener('input', () => this.compute());
      $('[data-result-pick]', this).addEventListener('click', () => {
        const unit = $('[data-main-product]')?._unit;
        if (unit) {
          const i = unit.fieldsets.findIndex((fs) => $$('input', fs).some((input) => input.value === this.size));
          if (i >= 0) unit.select(i, this.size);
        }
        this.closest('dialog')?.close();
      });
    }
    parse(guide) {
      const table = guide && $$('table', guide).find((t) => /height/i.test(t.textContent) && /weight/i.test(t.textContent));
      if (!table) return [];
      const range = (text, unit) => {
        const m = text.match(new RegExp(`(\\d+(?:\\.\\d+)?)\\s*[–-]\\s*(\\d+(?:\\.\\d+)?)\\s*${unit}`));
        return m ? [Number(m[1]), Number(m[2])] : null;
      };
      return $$('tbody tr', table).map((tr) => {
        const cells = $$('td', tr).map((td) => td.textContent);
        const h = range(cells[1] || '', 'cm');
        const w = range(cells[2] || '', 'kg');
        return h && w ? { size: cells[0].trim(), h, w } : null;
      }).filter(Boolean);
    }
    units() {
      this.imperial = $('input[name="finder-units"]:checked', this).value === 'imperial';
      $('[data-imperial]', this).hidden = !this.imperial;
      $('[data-metric]', this).hidden = this.imperial;
      this.compute();
    }
    compute() {
      const val = (sel) => Number($(sel, this).value) || 0;
      const cm = this.imperial ? (val('[data-ft]') * 12 + val('[data-in]')) * 2.54 : val('[data-cm]');
      const kg = this.imperial ? val('[data-lb]') * 0.4536 : val('[data-kg]');
      const result = $('[data-result]', this);
      if (cm < 120 || kg < 30) { result.hidden = true; return; }
      const rows = this.rows;
      const pickBy = (value, key) => {
        // the first size whose range holds the value; ranges overlap, so prefer the centre-most
        const inside = rows.filter((r) => value >= r[key][0] && value <= r[key][1]);
        if (inside.length) return inside.sort((a, b) => Math.abs((a[key][0] + a[key][1]) / 2 - value) - Math.abs((b[key][0] + b[key][1]) / 2 - value))[0];
        if (value < rows[0][key][0]) return rows[0];
        return rows[rows.length - 1];
      };
      const byH = pickBy(cm, 'h');
      const byW = pickBy(kg, 'w');
      const hi = rows.indexOf(byH);
      const wi = rows.indexOf(byW);
      const pick = rows[Math.max(hi, wi)];
      const out = cm > rows[rows.length - 1].h[1] + 6 || kg > rows[rows.length - 1].w[1] + 15 || cm < rows[0].h[0] - 8;
      this.size = pick.size;
      $$('[data-result-size]', this).forEach((el) => { el.textContent = pick.size; });
      $('[data-result-text]', this).textContent = this.strings.fit;
      $('[data-result-note]', this).textContent = out ? this.strings.out : hi !== wi ? this.strings.between.replace('[a]', rows[Math.min(hi, wi)].size).replace(/\[b\]/g, pick.size) : '';
      result.hidden = false;
    }
  });

  /* ---------- help desk: filter the questions as you type ---------- */
  customElements.define('faq-desk', class extends HTMLElement {
    connectedCallback() {
      const input = $('[data-desk-input]', this);
      const items = $$('[data-desk-q]', this);
      if (!input || !items.length) return;
      const groups = $$('[data-desk-group]', this);
      const none = $('[data-desk-none]', this);
      const texts = items.map((el) => el.textContent.toLowerCase());
      input.addEventListener('input', () => {
        const words = input.value.toLowerCase().split(/\s+/).filter(Boolean);
        let shown = 0;
        items.forEach((el, i) => {
          const ok = words.every((w) => texts[i].includes(w));
          el.hidden = !ok;
          if (ok) shown += 1;
          if (!words.length) el.open = false;
        });
        groups.forEach((group) => {
          let el = group.nextElementSibling;
          let any = false;
          while (el && !el.matches('[data-desk-group]')) {
            if (el.matches('[data-desk-q]') && !el.hidden) any = true;
            el = el.nextElementSibling;
          }
          group.hidden = !any;
        });
        if (none) none.hidden = shown > 0;
        if (words.length && shown === 1) items.find((el) => !el.hidden).open = true;
      });
    }
  });

  /* ---------- contact: the topic sets the hint and whether an order number is asked ---------- */
  customElements.define('contact-topics', class extends HTMLElement {
    connectedCallback() {
      const form = this.closest('form');
      const message = $('[data-message]', form);
      const order = $('[data-order-field]', form);
      const wanted = new URLSearchParams(location.search).get('topic');
      if (wanted) {
        const match = $$('input', this).find((i) => i.value.toLowerCase().includes(wanted.toLowerCase()));
        if (match) match.checked = true;
      }
      const apply = () => {
        const on = $('input:checked', this);
        if (!on) return;
        if (message) message.placeholder = on.dataset.hint || '';
        if (order) order.hidden = on.dataset.order !== 'true';
      };
      this.addEventListener('change', apply);
      apply();
    }
  });

  /* ---------- recently viewed: kept in this browser, drawn as a rail ---------- */
  const RECENT_KEY = 'artieRecent';
  const recentList = () => {
    try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]').filter((h) => typeof h === 'string'); } catch (e) { return []; }
  };
  customElements.define('recent-rail', class extends HTMLElement {
    async connectedCallback() {
      const track = $('[data-recent-track]', this);
      const handles = recentList().filter((h) => h !== this.dataset.current).slice(0, 8);
      if (!track || handles.length < (Number(this.dataset.min) || 1)) return;
      const cards = await Promise.all(handles.map(async (handle) => {
        try {
          const doc = await fetchDoc(`${this.dataset.root}products/${handle}?section_id=card-solo`);
          return $('.rail__item', doc);
        } catch (e) { return null; }
      }));
      const found = cards.filter(Boolean);
      if (found.length < (Number(this.dataset.min) || 1)) return;
      found.forEach((card) => track.appendChild(document.importNode(card, true)));
      this.hidden = false;
      initMotion(this);
    }
  });
  function rememberProduct() {
    const main = $('[data-main-product]');
    const handle = main && (main.dataset.url || '').split('/products/')[1];
    if (!handle) return;
    const clean = handle.split(/[?#]/)[0];
    try { localStorage.setItem(RECENT_KEY, JSON.stringify([clean, ...recentList().filter((h) => h !== clean)].slice(0, 12))); } catch (e) { /* private mode */ }
  }

  /* ---------- figures that count up ---------- */
  function initCounts(root = document) {
    $$('[data-count-up]', root).forEach((out) => {
      if (out._count) return;
      out._count = true;
      const target = Number(out.dataset.countUp) || 0;
      const box = out.parentElement;
      if (reduceMotion || !('IntersectionObserver' in window)) return;
      out.textContent = 0;
      box.style.setProperty('--w', 100);
      new IntersectionObserver(([entry], io) => {
        if (!entry.isIntersecting) return;
        io.disconnect();
        const start = performance.now();
        const step = (now) => {
          const t = clamp((now - start) / 1300);
          const e = 1 - (1 - t) ** 3;
          out.textContent = Math.round(target * e);
          box.style.setProperty('--w', Math.round(100 + 800 * e));
          if (t < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      }, { threshold: 0.6 }).observe(box);
    });
  }

  /* ---------- delivery window on the product page, in business days from today ---------- */
  function initEta(root = document) {
    $$('[data-eta]', root).forEach((el) => {
      const addDays = (n) => {
        const d = new Date();
        let left = n;
        while (left > 0) {
          d.setDate(d.getDate() + 1);
          const day = d.getDay();
          if (day !== 0 && day !== 6) left -= 1;
        }
        return d.toLocaleDateString(document.documentElement.lang || 'en', { month: 'short', day: 'numeric' });
      };
      const min = Number(el.dataset.min);
      const max = Number(el.dataset.max);
      if (!max || max < min) return;
      $('span', el).textContent = el.dataset.text.replace('[from]', addDays(min)).replace('[to]', addDays(max));
      el.hidden = false;
    });
  }

  /* ---------- collection: filters, sort, density, load more ---------- */
  const DENSITY_KEY = 'artieDensity';
  function applyDensity() {
    let density = 'dense';
    try { density = localStorage.getItem(DENSITY_KEY) || 'dense'; } catch (e) { /* private mode */ }
    $$('[data-grid]').forEach((grid) => { grid.dataset.density = density; });
    $$('[data-density]').forEach((btn) => btn.setAttribute('aria-pressed', String(btn.dataset.density === density)));
  }
  document.addEventListener('click', (event) => {
    const btn = event.target.closest('button[data-density]');
    if (!btn) return;
    try { localStorage.setItem(DENSITY_KEY, btn.dataset.density); } catch (e) { /* private mode */ }
    applyDensity();
  });

  customElements.define('facet-results', class extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      let timer;
      this.addEventListener('change', (e) => {
        if (!e.target.closest('[data-facet-form]')) return;
        clearTimeout(timer);
        timer = setTimeout(() => this.submit(), e.target.type === 'number' ? 500 : 0);
      });
      this.addEventListener('submit', (e) => {
        if (e.target.matches('[data-facet-form]')) { e.preventDefault(); this.submit(); }
      });
      this.addEventListener('click', (e) => {
        const link = e.target.closest('[data-facet-link]');
        if (link) { e.preventDefault(); this.load(link.href, true); return; }
        const more = e.target.closest('[data-more-link]');
        if (more) { e.preventDefault(); this.more(more); }
      });
      window.addEventListener('popstate', () => this.load(location.href, false));
    }
    submit() {
      const form = $('[data-facet-form]', this);
      const params = new URLSearchParams();
      for (const [key, value] of new FormData(form)) if (value !== '') params.append(key, value);
      this.load(`${location.pathname}?${params}`, true);
    }
    async load(url, push) {
      const drawer = $('#FacetDrawer', this);
      const wasOpen = drawer && drawer.open;
      const openGroups = drawer ? $$('details', drawer).map((d) => d.open) : [];
      const scroll = drawer ? $('.drawer__body', drawer).scrollTop : 0;
      this.classList.add('is-busy');
      try {
        const doc = await fetchDoc(url);
        const fresh = $('facet-results', doc);
        if (!fresh) throw new Error('empty');
        if (wasOpen) drawer.close();
        this.innerHTML = fresh.innerHTML;
        if (push) history.pushState({}, '', url);
        applyDensity();
        initMotion(this);
        if (wasOpen) {
          const next = $('#FacetDrawer', this);
          next.style.transition = 'none';
          next.showModal();
          $$('details', next).forEach((d, i) => { if (openGroups[i] !== undefined) d.open = openGroups[i]; });
          $('.drawer__body', next).scrollTop = scroll;
          requestAnimationFrame(() => next.style.removeProperty('transition'));
        }
      } catch (e) {
        location.href = url;
      } finally {
        this.classList.remove('is-busy');
      }
    }
    async more(link) {
      link.classList.add('is-loading');
      try {
        const doc = await fetchDoc(link.href);
        const cards = $$('[data-grid] > *', doc);
        $('[data-grid]', this).append(...cards);
        initMotion(this);
        const nav = $('[data-more]', this);
        const freshNav = $('[data-more]', doc);
        if (freshNav) { $('.more__prev', freshNav)?.remove(); nav.replaceWith(freshNav); } else nav.remove();
      } catch (e) {
        location.href = link.href;
      }
    }
  });

  /* ---------- predictive search ---------- */
  customElements.define('predictive-search', class extends HTMLElement {
    connectedCallback() {
      this.input = $('input[type="search"]', this);
      this.results = $('[data-results]', this);
      this.initial = this.results.innerHTML;
      let timer;
      this.input.addEventListener('input', () => {
        clearTimeout(timer);
        timer = setTimeout(() => this.search(), 220);
      });
      this.closest('dialog')?.addEventListener('dialog:open', () => setTimeout(() => this.input.focus(), 60));
    }
    async search() {
      const q = this.input.value.trim();
      if (q.length < 2) { this.results.innerHTML = this.initial; return; }
      this.controller?.abort();
      this.controller = new AbortController();
      try {
        const url = `${A.routes.predictive}?q=${encodeURIComponent(q)}&resources[type]=product,collection,query&resources[limit]=6&section_id=predictive-search`;
        const res = await fetch(url, { signal: this.controller.signal });
        if (!res.ok) return;
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const section = $('.shopify-section', doc) || doc.body;
        this.results.innerHTML = section.innerHTML;
      } catch (e) { /* aborted or offline */ }
    }
  });

  /* ---------- motion: one scroll loop, only for what is on screen ---------- */
  const clamp = (v, lo = 0, hi = 1) => Math.min(hi, Math.max(lo, v));
  const Motion = (() => {
    const items = new Set();
    let ticking = false;
    const run = () => {
      ticking = false;
      const vh = window.innerHeight;
      items.forEach((item) => { if (item.el._fxOn) item.update(item.el.getBoundingClientRect(), vh); });
    };
    const request = () => { if (!ticking) { ticking = true; requestAnimationFrame(run); } };
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => { entry.target._fxOn = entry.isIntersecting; });
      request();
    }, { rootMargin: '30% 0px 30% 0px' });
    window.addEventListener('scroll', request, { passive: true });
    window.addEventListener('resize', request);
    return { add(el, update) { items.add({ el, update }); io.observe(el); }, request };
  })();

  // headings: split into the lines they wrap to, so each line can change weight without re-wrapping
  function weigh(el) {
    const text = (el.dataset.text || el.textContent).trim().replace(/\s+/g, ' ');
    if (!text) return;
    el.dataset.text = text;
    const esc = (t) => t.replace(/&/g, '&amp;').replace(/</g, '&lt;');
    const split = () => {
      el.classList.remove('is-weighing');
      el.innerHTML = text.split(' ').map((w) => `<span>${esc(w)}</span>`).join(' ');
      const lines = [];
      let top = null;
      [...el.children].forEach((word) => {
        if (word.offsetTop !== top) { lines.push([]); top = word.offsetTop; }
        lines[lines.length - 1].push(word.textContent);
      });
      el.setAttribute('aria-label', text);
      el.innerHTML = lines.map((line) => `<span class="ln" aria-hidden="true">${esc(line.join(' '))}</span>`).join('');
      el._lines = [...el.children];
      el.classList.add('is-weighing');
      paint(el.getBoundingClientRect(), window.innerHeight);
    };
    const paint = (rect, vh) => {
      const p = (vh - rect.top) / (vh * 0.5);
      el._lines.forEach((line, i) => line.style.setProperty('--k', clamp(p * 1.25 - i * 0.24).toFixed(3)));
    };
    split();
    // re-wrap only when the window itself changes width: the heading's own width moves with its weight,
    // so watching the heading would make it rebuild (and flash) in the middle of the animation
    let width = window.innerWidth;
    window.addEventListener('resize', () => {
      if (window.innerWidth === width) return;
      width = window.innerWidth;
      split();
    });
    if (document.fonts && document.fonts.status !== 'loaded') document.fonts.ready.then(split);
    Motion.add(el, paint);
  }

  const fx = {
    hero(el) {
      Motion.add(el, (rect) => el.style.setProperty('--p', clamp(-rect.top / rect.height).toFixed(3)));
    },
    lineup(el) {
      const stage = $('.lineup__stage', el);
      const track = $('[data-lineup-track]', el);
      const title = $('[data-lineup-title]', el);
      let travel = 0; // how far the row moves
      let run = 0; // how much page scroll that takes: less on phones, where a screen is short
      let titleTravel = 0;
      const measure = () => {
        el.classList.add('is-pinned');
        travel = Math.max(0, track.scrollWidth - stage.clientWidth);
        run = travel * (window.matchMedia('(max-width: 749px)').matches ? 0.6 : 1);
        titleTravel = Math.max(0, title.offsetWidth - stage.clientWidth * 0.5);
        el.style.height = `calc(100svh + ${run}px)`;
        Motion.request();
      };
      measure();
      window.addEventListener('load', measure);
      let w = window.innerWidth;
      window.addEventListener('resize', () => { if (window.innerWidth !== w) { w = window.innerWidth; measure(); } });
      // Sideways input drives the same scroll: a swipe on touch screens, a horizontal wheel on trackpads.
      // While it does, the position is kept as a decimal number and drawn in the same frame, so the row
      // follows the finger exactly; the page scroll only follows along to keep the section pinned.
      const hint = $('.lineup__hint', el);
      const bar = $('[data-lineup-bar]', el);
      const root = document.documentElement;
      let drawn = 0;
      let settle = false;
      const draw = (p) => {
        drawn = p * travel;
        track.style.transform = `translate3d(${(-p * travel).toFixed(2)}px,0,0)`;
        title.style.transform = `translate3d(${(-p * titleTravel).toFixed(2)}px,0,0)`;
        if (hint) hint.style.setProperty('--p', p.toFixed(3));
        if (bar) bar.style.setProperty('--p', p.toFixed(3));
      };
      let driving = false;
      let pos = 0; // page scroll position while driving, with decimals
      let origin = 0; // page scroll position at which the section pins
      let pending = 0;
      let frame = 0;
      let glide = 0;
      let idle = 0;
      const take = () => {
        if (driving) return;
        driving = true;
        pos = window.scrollY;
        origin = pos + el.getBoundingClientRect().top;
        const at = drawn * (run / Math.max(1, travel));
        if (drawn > 0 && Math.abs(pos - origin - at) < 1.5) pos = origin + at; // carry on from the exact spot the last swipe left
        root.classList.add('is-driven');
      };
      const release = () => {
        if (driving) settle = true;
        driving = false;
        root.classList.remove('is-driven');
      };
      const move = (delta) => {
        // delta is in pixels of the row; the page scrolls by the matching share of its run
        const next = clamp(pos + delta * (run / Math.max(1, travel)), Math.min(pos, origin), Math.max(pos, origin + run));
        if (next === pos) return false;
        pos = next;
        window.scrollTo(0, pos);
        draw(clamp((pos - origin) / Math.max(1, run)));
        return true;
      };
      const flush = () => {
        frame = 0;
        if (pending) { move(pending); pending = 0; }
      };
      const push = (delta) => {
        pending += delta;
        if (!frame) frame = requestAnimationFrame(flush);
      };
      let startX = 0;
      let startY = 0;
      let lastX = 0;
      let axis = null;
      let trail = [];
      stage.addEventListener('touchstart', (e) => {
        const t = e.touches[0];
        startX = lastX = t.clientX;
        startY = t.clientY;
        axis = null;
        trail = [];
        cancelAnimationFrame(glide);
        release();
      }, { passive: true });
      stage.addEventListener('touchmove', (e) => {
        const t = e.touches[0];
        if (axis === null) {
          const dx = Math.abs(t.clientX - startX);
          const dy = Math.abs(t.clientY - startY);
          if (dx < 6 && dy < 6) return;
          axis = dx > dy ? 'x' : 'y';
          lastX = t.clientX;
          if (axis === 'x') take();
        }
        if (axis !== 'x') return;
        push(lastX - t.clientX);
        lastX = t.clientX;
        const now = performance.now();
        trail.push({ x: t.clientX, t: now });
        while (trail.length > 2 && now - trail[0].t > 100) trail.shift();
      }, { passive: true });
      const letGo = () => {
        if (axis !== 'x') return;
        axis = null;
        if (frame) { cancelAnimationFrame(frame); flush(); }
        const now = performance.now();
        const first = trail[0];
        const last = trail[trail.length - 1];
        // speed of the finger over its last 100ms, in px per ms; none if it had stopped before lifting
        let v = first && last && last.t > first.t && now - last.t < 80 ? (first.x - last.x) / (last.t - first.t) : 0;
        v = clamp(v, -5, 5);
        let then = now;
        const coast = (time) => {
          const dt = Math.min(48, Math.max(1, time - then));
          then = time;
          const moved = move(v * dt);
          v *= Math.pow(0.998, dt);
          if (!moved || Math.abs(v) < 0.02) { release(); return; }
          glide = requestAnimationFrame(coast);
        };
        if (Math.abs(v) < 0.02) { release(); return; }
        glide = requestAnimationFrame(coast);
      };
      stage.addEventListener('touchend', letGo);
      stage.addEventListener('touchcancel', letGo);
      stage.addEventListener('wheel', (e) => {
        if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
        e.preventDefault();
        take();
        push(e.deltaMode === 1 ? e.deltaX * 16 : e.deltaX);
        clearTimeout(idle);
        idle = setTimeout(release, 140);
      }, { passive: false });
      Motion.add(el, (rect) => {
        if (driving) return;
        const p = clamp(-rect.top / Math.max(1, run));
        // the page scroll lands on whole pixels, the swipe does not: do not step back by that fraction when it ends
        if (settle) { settle = false; if (Math.abs(p * travel - drawn) < 1.5) return; }
        draw(p);
      });
    },
    fit(el) {
      Motion.add(el, (rect, vh) => {
        el.style.setProperty('--in', clamp((vh * 0.8 - rect.top) / (vh * 0.5)).toFixed(3));
        el.style.setProperty('--p', clamp((vh - rect.top) / (vh + rect.height)).toFixed(3));
      });
    },
    index(el) {
      const rows = $$('.index__row', el);
      const pointer = window.matchMedia('(hover: hover) and (min-width: 750px)');
      rows.forEach((row) => row.addEventListener('pointermove', (e) => {
        if (!pointer.matches) return;
        const r = row.getBoundingClientRect();
        row.style.setProperty('--x', `${Math.min(e.clientX - r.left, r.width - 320)}px`);
        row.style.setProperty('--y', `${e.clientY - r.top}px`);
      }));
      Motion.add(el, (rect, vh) => {
        if (pointer.matches) { el.classList.remove('is-live'); return; }
        el.classList.add('is-live');
        const mid = vh * 0.46;
        let best = null;
        let dist = Infinity;
        rows.forEach((row) => {
          const r = row.getBoundingClientRect();
          const d = Math.abs(r.top + r.height / 2 - mid);
          if (d < dist) { dist = d; best = row; }
        });
        rows.forEach((row) => row.classList.toggle('is-active', row === best && dist < vh * 0.3));
      });
    }
  };

  const seen = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-seen');
      seen.unobserve(entry.target);
    });
  }, { threshold: 0.35 });

  const stand = new IntersectionObserver((entries) => {
    let n = 0;
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      const card = entry.target;
      card.style.setProperty('--n', n++);
      card.classList.add('is-in');
      stand.unobserve(card);
      setTimeout(() => card.style.removeProperty('--n'), 1400);
    });
  }, { rootMargin: '0px 0px -6% 0px' });

  function weighIn() {
    const box = $('[data-weighin]');
    const desc = $('.pdp__desc');
    if (!box || !desc) return;
    const match = desc.textContent.match(/(\d{3})\s?(?:gsm|g)\b/i);
    if (!match) return;
    const target = Number(match[1]);
    const out = $('[data-weighin-n]', box);
    box.hidden = false;
    if (reduceMotion) { out.textContent = target; return; }
    box.style.setProperty('--w', 100);
    new IntersectionObserver(([entry], io) => {
      if (!entry.isIntersecting) return;
      io.disconnect();
      const start = performance.now();
      const step = (now) => {
        const t = clamp((now - start) / 1300);
        const e = 1 - (1 - t) ** 3;
        out.textContent = Math.round(target * e);
        box.style.setProperty('--w', Math.round(100 + 800 * e));
        if (t < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    }, { threshold: 0.8 }).observe(box);
  }

  function initMotion(root = document) {
    const cards = $$('.card:not(.is-in)', root);
    const notes = $$('[data-note]:not(.is-seen)', root);
    if (reduceMotion || !('IntersectionObserver' in window)) {
      cards.forEach((c) => c.classList.add('is-in'));
      notes.forEach((n) => n.classList.add('is-seen'));
      return;
    }
    cards.forEach((c) => stand.observe(c));
    notes.forEach((n) => seen.observe(n));
    $$('[data-fx]', root).forEach((el) => {
      if (el._fx) return;
      el._fx = true;
      fx[el.dataset.fx]?.(el);
    });
    const headings = $$('[data-weigh]', root).filter((el) => !el._weigh);
    headings.forEach((el) => { el._weigh = true; });
    (document.fonts?.ready || Promise.resolve()).then(() => headings.forEach(weigh));
  }

  /* tape band: drifts on its own, and scrolling pushes it along */
  customElements.define('tape-band', class extends HTMLElement {
    connectedCallback() {
      if (reduceMotion) return;
      this.track = $('.tape__track', this);
      const group = $('.tape__group', this);
      if (!group) return;
      const fill = () => {
        this.groupWidth = group.offsetWidth;
        if (!this.groupWidth) return;
        const need = Math.ceil((window.innerWidth * 1.2) / this.groupWidth) + 1;
        while (this.track.children.length < need) this.track.append(group.cloneNode(true));
      };
      (document.fonts?.ready || Promise.resolve()).then(fill);
      window.addEventListener('resize', fill);
      let x = 0;
      let lastY = window.scrollY;
      let last = 0;
      let push = 0;
      let dir = 1;
      let running = false;
      const loop = (now) => {
        if (!running) return;
        const dt = Math.min(64, now - (last || now));
        last = now;
        const y = window.scrollY;
        const delta = y - lastY;
        lastY = y;
        if (delta) dir = delta > 0 ? 1 : -1;
        push = push * 0.9 + delta * 0.6;
        x += dir * dt * 0.045 + push;
        if (this.groupWidth) {
          const offset = ((x % this.groupWidth) + this.groupWidth) % this.groupWidth;
          this.track.style.transform = `translate3d(${-offset.toFixed(1)}px,0,0)`;
        }
        requestAnimationFrame(loop);
      };
      new IntersectionObserver(([entry]) => {
        const on = entry.isIntersecting;
        if (on && !running) { running = true; last = 0; lastY = window.scrollY; requestAnimationFrame(loop); }
        if (!on) running = false;
      }).observe(this);
    }
  });

  // the clicked product photo keeps its identity across the page change
  document.addEventListener('click', (event) => {
    const link = event.target.closest('.card a[href]');
    if (!link || /variant=/.test(link.href)) return;
    const img = $('[data-card-img]', link.closest('.card'));
    if (img) img.style.viewTransitionName = 'product-media';
  }, true);

  /* ---------- small things ---------- */
  document.addEventListener('change', (event) => {
    if (event.target.matches('[data-autosubmit]')) event.target.form.submit();
  });

  function init() {
    initProducts();
    initBuyBar();
    applyDensity();
    initMotion();
    weighIn();
    initCounts();
    initEta();
    rememberProduct();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // theme editor: re-run after a section is re-rendered
  document.addEventListener('shopify:section:load', (event) => {
    initProducts(event.target);
    applyDensity();
    initMotion(event.target);
    initCounts(event.target);
  });
})();
